import {Component, OnDestroy, OnInit} from "@angular/core";
import {RmOrSampleMultiModificationDto, RmOrSampleMultiModificationStatus, RmOrSampleMultiModificationStore} from "@usercommon/stores/rm-or-sample-multi-modification.store";
import {Subscription} from "rxjs";
import {List} from "immutable";
import {Router} from "@angular/router";
import {ValidationService} from '@sharedcommon/services/validation.service';
import {MenuItem} from "primeng/api/menuitem";
import { UserService } from "@sharedcommon/services/user.service";

@Component({
  selector: 'neq-rm-or-sample-multimodification',
  templateUrl: './rm-or-sample-multimodification.component.html',
  styleUrls: ['./rm-or-sample-multimodification.component.scss']
})
export class RmOrSampleMultimodificationComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  private toProcessSubscription: Subscription;
  private savedSubscription: Subscription;
  private multiModificationDtos: List<RmOrSampleMultiModificationDto>;
  /**
   * Here to avoid to ask confirmation to change page at the first selected item
   */
  private overrideModifFormCheck: boolean = true;

  constructor(
    private readonly rmMultiModificationStore: RmOrSampleMultiModificationStore,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly validationService: ValidationService
  ) {
  }

  ngOnDestroy(): void {
    this.toProcessSubscription?.unsubscribe();
    this.savedSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.toProcessSubscription = this.rmMultiModificationStore.rmOrSamplesToProcess
      .subscribe((rms: List<RmOrSampleMultiModificationDto>) => {
        if (rms == null || rms.isEmpty()) {
          this.items = [];
          this.multiModificationDtos = List();
          return;
        }
        this.multiModificationDtos = rms;
        this.items = rms.map((r: RmOrSampleMultiModificationDto) => {
          return {
            label: r.label,
            styleClass: this.getCssClassFromStatus(r.state),
            command: async (event: any) => {
              this.goToRmOrSampleInputPage(event.item.id);
            },
            id: r.id
          }
        }).toArray();
        this.next();
      });

    this.savedSubscription = this.rmMultiModificationStore.rmOrSampleSaved
      .subscribe((savedRmId: string) => {
        if (savedRmId != null && savedRmId.trim().length > 0) {
          this.multiModificationDtos.filter(i => {
            return i.id == savedRmId;
          }).first()
            .state = RmOrSampleMultiModificationStatus.MODIFIED;
          this.items.filter(i => {
            return i.id == savedRmId;
          }).forEach(i => i.styleClass = this.getCssClassFromStatus(RmOrSampleMultiModificationStatus.MODIFIED));
        }
      });
  }

  next(): void {
    if (this.rmMultiModificationStore.currentRmOrSampleId == undefined) {
      let next = this.getFirstNotModifiedFromTop();
      if (next != null) {
        this.goToRmOrSampleInputPage(next.id)
      }
      return;
    }

    let next: RmOrSampleMultiModificationDto = this.getNextRmOrSampleFromCurrentSelected();
    if (next == null) {
      // get first not modified RM from top of the selected rm list
      next = this.getFirstNotModifiedFromTop();
    }
    if (next != null) {
      // go to the next rm if there is one
      this.goToRmOrSampleInputPage(next.id);
    }
  }

  deactivateNextButton(): boolean {
    return this.multiModificationDtos.filter(i => {
      return i.state == RmOrSampleMultiModificationStatus.NOT_REACHED;
    }).count() == 0;
  }

  private goToRmOrSampleInputPage(id: string) {
    const callback = async () => {
      this.setNotModifiedStateToItem(id);
      this.rmMultiModificationStore.currentRmOrSampleId = id;
      await this.router.navigate([ `${(this.userService.isFeedOperatingMode() ? '/app/rawmaterials/input/' : '/app/samples/input/')}${id}`]);
    };
    if (this.overrideModifFormCheck) {
      callback();
      // like that at the next navigation the modification check will be done
      this.overrideModifFormCheck = false;
    } else {
      // confirm before quit
      this.validationService.checkModifications(callback);
    }
  }

  private setNotModifiedStateToItem(id: string): void {
    this.items.filter(i => {
      return i.id === id;
    })
      .forEach(i => i.styleClass = this.getCssClassFromStatus(RmOrSampleMultiModificationStatus.NOT_MODIFIED));

    this.multiModificationDtos.filter(i => {
      return i.id == id;
    }).first()
      .state = RmOrSampleMultiModificationStatus.NOT_MODIFIED;
  }

  /**
   * Select next not modified RM, from the current edited rm.
   * if no unmodified rm is found of the current rm at the end of the list, then return null.
   * @private
   */
  private getNextRmOrSampleFromCurrentSelected(): RmOrSampleMultiModificationDto {
    const nb = this.multiModificationDtos.size;
    let currentIndex = -1;
    // Get index of current selected.
    for (let _i = 0; _i < nb; _i++) {
      if (this.multiModificationDtos.get(_i).id == this.rmMultiModificationStore.currentRmOrSampleId) {
        currentIndex = _i;
        break;
      }
    }

    let next: RmOrSampleMultiModificationDto = null;

    if (currentIndex != -1) {
      // get first not modified RM from current selected RM
      for (let _i = currentIndex + 1; _i < nb; _i++) {
        let rm = this.multiModificationDtos.get(_i);
        if (rm.state == RmOrSampleMultiModificationStatus.NOT_REACHED) {
          next = rm;
          break;
        }
      }
    }
    return next;
  }

  private getFirstNotModifiedFromTop(): RmOrSampleMultiModificationDto {
    return this.multiModificationDtos.filter(i => {
      return i.state == RmOrSampleMultiModificationStatus.NOT_REACHED;
    }).first()
  }

  private getCssClassFromStatus(state: RmOrSampleMultiModificationStatus): string {
    switch (state) {
      case 'Not reached':
        return 'option-not-reached';
      case 'Not modified':
        return 'option-not-modified';
      case 'Modified':
        return 'option-modified';
      default:
        return '';
    }
  }
}
